import { useState } from 'react';
import { Close } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Tab, Tabs } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InvoiceFragmentFragment, useUserQuery } from '../../api/gql';
import { InvoiceFormType } from '../forms/invoice/types';
import { CustomersOverview } from './customers';
import { VendorsOverview } from './vendors';

export const ConnectedAutoFillMe = () => {
  const contextMethods = useFormContext<InvoiceFormType>();
  const { data, isLoading } = useUserQuery();

  if (!contextMethods) {
    throw new Error(
      'Using `ConnectedAutoFillMe` is allowed only inside react-hook-form context. Place `ConnectedAutoFillMe` as child of `FormProvider`',
    );
  }

  const onClick = () => {
    if (!data) {
      return;
    }

    data.user.businessName && contextMethods.setValue('vendor.name', data.user.businessName);
    data.user.vatId && contextMethods.setValue('vendor.vatId', data.user.vatId);
    data.user.taxId && contextMethods.setValue('vendor.taxId', data.user.taxId);
    contextMethods.setValue(
      'vendor.address',
      [data.user.streetAddress, data.user.zipCode, data.user.city, data.user.country].filter((e) => e).join('\n'),
    );
    data.user.bankAccount && contextMethods.setValue('bankAccount', data.user.bankAccount);
    data.user.bankNumber && contextMethods.setValue('bankNumber', data.user.bankNumber);
    data.user.iban && contextMethods.setValue('iban', data.user.iban);
    data.user.swift && contextMethods.setValue('swift', data.user.swift);
  };

  return (
    <LoadingButton disabled={!data} loading={isLoading} onClick={onClick} variant={'text'}>
      Načíst mé údaje
    </LoadingButton>
  );
};

const isCustomer = (contact: CustomerContact | VendorContact): contact is CustomerContact =>
  !!(contact as CustomerContact)?.email;

export const ConnectedAutoFillVendorsButton = () => {
  const contextMethods = useFormContext<InvoiceFormType>();

  if (!contextMethods) {
    throw new Error(
      'Using `ConnectedAutoFillVendorsButton` is allowed only inside react-hook-form context. Place `ConnectedAutoFillVendorsButton` as child of `FormProvider`',
    );
  }

  const handleContactChoose = (contact: CustomerContact | VendorContact) => {
    contextMethods.setValue('vendor.name', contact?.name ?? '');
    contextMethods.setValue('vendor.address', contact?.address ?? '');
    contextMethods.setValue('vendor.vatId', contact?.vatId ?? '');
    contextMethods.setValue('vendor.taxId', contact?.taxId ?? '');
  };

  return <AutoFillContactsButton onContactChoose={handleContactChoose} />;
};

export const ConnectedAutoFillCustomersButton = () => {
  const contextMethods = useFormContext<InvoiceFormType>();

  if (!contextMethods) {
    throw new Error(
      'Using `ConnectedAutoFillCustomersButton` is allowed only inside react-hook-form context. Place `ConnectedAutoFillCustomersButton` as child of `FormProvider`',
    );
  }

  const handleContactChoose = (contact: CustomerContact | VendorContact) => {
    contextMethods.setValue('customer.name', contact?.name ?? '');
    contextMethods.setValue('customer.address', contact?.address ?? '');
    contextMethods.setValue('customer.vatId', contact?.vatId ?? '');
    contextMethods.setValue('customer.taxId', contact?.taxId ?? '');
    if (isCustomer(contact)) {
      contextMethods.setValue('customer.email', contact?.email ?? '');
      contextMethods.setValue('customer.phoneNumber', contact?.phoneNumber ?? '');
      contact?.remindingTone && contextMethods.setValue('customer.remindingTone', contact.remindingTone);
    }
  };

  return <AutoFillContactsButton onContactChoose={handleContactChoose} />;
};

type CustomerContact = InvoiceFragmentFragment['customer'];
type VendorContact = InvoiceFragmentFragment['vendor'];

type AutoFillContactsProps = {
  onContactChoose: (customer: CustomerContact | VendorContact) => void;
};

export const AutoFillContactsButton = ({ onContactChoose }: AutoFillContactsProps) => {
  const [isOpen, setOpen] = useState(false);

  const handleContactChoose = (contact: CustomerContact | VendorContact) => {
    onContactChoose(contact);
    setOpen(false);
  };

  return (
    <>
      <Button
        variant={'text'}
        onClick={() => {
          setOpen(true);
        }}
      >
        Předvyplnit z adresáře
      </Button>
      <AutoFillContactsModal open={isOpen} onClose={() => setOpen(false)} onContactChoose={handleContactChoose} />
    </>
  );
};

type AutoFillContactsModalProps = {
  open: boolean;
  onClose: () => void;
  onContactChoose: (customer: CustomerContact | VendorContact) => void;
};

export const AutoFillContactsModal = ({ onContactChoose, open, onClose }: AutoFillContactsModalProps) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={'xl'}>
      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} py={1} px={2}>
        <DialogTitle>Adresář kontaktů</DialogTitle>
        <IconButton size={'large'} onClick={onClose}>
          <Close />
        </IconButton>
      </Box>
      <DialogContent>
        <AutoFillContacts onContactChoose={onContactChoose} />
      </DialogContent>
    </Dialog>
  );
};

type AutoFillContacts = {
  onContactChoose: (customer: CustomerContact | VendorContact) => void;
};

export const AutoFillContacts = ({ onContactChoose }: AutoFillContacts) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(parseInt(newValue));
  };

  const handleContactChoose = (contact: CustomerContact | VendorContact) => {
    onContactChoose(contact);
    setSelectedTab(0);
  };

  return (
    <Box>
      <Tabs
        variant="fullWidth"
        value={selectedTab}
        onChange={handleChange}
        aria-label="customers and vendors navigation"
      >
        <Tab label={t('customers')} />
        <Tab label={t('vendors')} />
      </Tabs>
      <Box>{selectedTab === 0 && <CustomersOverview onCustomerChoose={handleContactChoose} />}</Box>
      <Box>{selectedTab === 1 && <VendorsOverview onVendorChoose={handleContactChoose} />}</Box>
    </Box>
  );
};
